import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { SimpleDropdown } from "./ui/simple-dropdown";
import ChakraDatePicker from "./ui/chakra-datepicker";
import { IMaskInput } from "react-imask";
import { X, Save, User, Briefcase, IdCard } from "lucide-react";
import { toast } from "react-hot-toast";
import { ParentEditRequest, ParentRelationType } from "../api/types";
import { educationAPI } from "../api/axiosClient";

interface ParentEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  parentId: string | null;
  onSuccess: () => void;
}

const ParentEditForm: React.FC<ParentEditFormProps> = ({
  isOpen,
  onClose,
  parentId,
  onSuccess,
}) => {
  const handleClose = () => {
    lastLoadedParentId.current = null;
    onClose();
  };

  const [formData, setFormData] = useState<ParentEditRequest>({
    id: "",
    userId: "",
    firstName: "",
    lastName: "",
    identityNumber: "",
    occupation: "",
    relationType: ParentRelationType.Mother,
    birthDate: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const lastLoadedParentId = useRef<string | null>(null);

  // Load parent data when parentId changes
  useEffect(() => {
    const loadParentData = async () => {
      if (!parentId) {
        return;
      }

      // Aynı ID için zaten yükleme yapıldıysa tekrar yapma
      if (lastLoadedParentId.current === parentId) {
        return;
      }

      lastLoadedParentId.current = parentId;
      setIsLoading(true);
      try {
        const response = await educationAPI.getParentDetails(parentId);

        if (response.success && response.data) {
          const data = response.data as any; // API response structure

          // Format birthDate for HTML date input (YYYY-MM-DD)
          let formattedBirthDate = "";
          if (data.birthDate) {
            // Parse the date and format it to avoid timezone issues
            const date = new Date(data.birthDate);
            // Use local date components to avoid timezone conversion
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            formattedBirthDate = `${year}-${month}-${day}`;
          }

          setFormData({
            id: data.id,
            userId: data.userId,
            firstName: data.firstName,
            lastName: data.lastName,
            identityNumber: data.identityNumber,
            occupation: data.occupation,
            relationType:
              data.relationType === "Mother"
                ? ParentRelationType.Mother
                : data.relationType === "Father"
                  ? ParentRelationType.Father
                  : data.relationType === "Guardian"
                    ? ParentRelationType.Guardian
                    : ParentRelationType.Other,
            birthDate: formattedBirthDate,
          });
        } else {
          toast.error("Veli bilgileri yüklenirken hata oluştu");
        }
      } catch (error) {
        console.error("Error loading parent data:", error);
        toast.error("Veli bilgileri yüklenirken hata oluştu");
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && parentId) {
      loadParentData();
    }
  }, [isOpen, parentId]);

  const relationTypes = useMemo(
    () => [
      { id: "1", label: "Anne", value: ParentRelationType.Mother },
      { id: "2", label: "Baba", value: ParentRelationType.Father },
      { id: "3", label: "Vasi", value: ParentRelationType.Guardian },
      { id: "4", label: "Diğer", value: ParentRelationType.Other },
    ],
    [],
  );

  const selectedRelationType = useMemo(() => {
    return (
      relationTypes.find((rt) => rt.value === formData.relationType) || null
    );
  }, [formData.relationType, relationTypes]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = "Ad alanı zorunludur";
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = "Soyad alanı zorunludur";
    }

    if (!formData.identityNumber?.trim()) {
      newErrors.identityNumber = "TC Kimlik No alanı zorunludur";
    } else if (!/^\d{11}$/.test(formData.identityNumber.replace(/\D/g, ""))) {
      newErrors.identityNumber = "TC Kimlik No 11 haneli olmalıdır";
    }

    if (!formData.occupation?.trim()) {
      newErrors.occupation = "Meslek alanı zorunludur";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Remove non-digits from identity number
      const cleanIdentityNumber =
        formData.identityNumber?.replace(/\D/g, "") || "";

      const request = {
        id: formData.id,
        userId: formData.userId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        identityNumber: cleanIdentityNumber,
        occupation: formData.occupation,
        relationType: formData.relationType,
        birthDate: formData.birthDate,
      };

      const response = await educationAPI.updateParent(formData.id, request);

      if (response.success) {
        toast.success(response.message || "Veli başarıyla güncellendi");
        onSuccess();
        handleClose();
      } else {
        toast.error(response.message || "Veli güncellenirken hata oluştu");
      }
    } catch (error) {
      console.error("Error updating parent:", error);
      toast.error("Veli güncellenirken hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    field: keyof ParentEditRequest,
    value: string | number,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl font-semibold">
              Veli Düzenle
            </CardTitle>
            <CardDescription>Veli bilgilerini güncelleyin</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ad */}
                <div>
                  <Label
                    htmlFor="firstName"
                    className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Ad *
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName || ""}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    className={errors.firstName ? "border-red-500" : ""}
                    placeholder="Adınızı girin"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.firstName}
                    </p>
                  )}
                </div>

                {/* Soyad */}
                <div>
                  <Label
                    htmlFor="lastName"
                    className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Soyad *
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName || ""}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    className={errors.lastName ? "border-red-500" : ""}
                    placeholder="Soyadınızı girin"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.lastName}
                    </p>
                  )}
                </div>

                {/* TC Kimlik No */}
                <div>
                  <Label
                    htmlFor="identityNumber"
                    className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <IdCard className="w-4 h-4 mr-2" />
                    TC Kimlik No *
                  </Label>
                  <IMaskInput
                    mask="00000000000"
                    placeholder="12345678901"
                    value={formData.identityNumber || ""}
                    onAccept={(value: string) =>
                      handleInputChange("identityNumber", value)
                    }
                    className={`w-full text-left text-sm font-mono px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400 ${errors.identityNumber ? "border-red-500" : ""}`}
                  />
                  {errors.identityNumber && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.identityNumber}
                    </p>
                  )}
                </div>

                {/* Meslek */}
                <div>
                  <Label
                    htmlFor="occupation"
                    className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Meslek *
                  </Label>
                  <Input
                    id="occupation"
                    type="text"
                    value={formData.occupation || ""}
                    onChange={(e) =>
                      handleInputChange("occupation", e.target.value)
                    }
                    className={errors.occupation ? "border-red-500" : ""}
                    placeholder="Mesleğinizi girin"
                  />
                  {errors.occupation && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.occupation}
                    </p>
                  )}
                </div>

                {/* Yakınlık Derecesi */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Yakınlık Derecesi *
                  </Label>
                  <SimpleDropdown
                    options={relationTypes}
                    value={selectedRelationType}
                    onChange={(option) =>
                      handleInputChange(
                        "relationType",
                        option?.value || ParentRelationType.Mother,
                      )
                    }
                    placeholder="Yakınlık derecesi seçin"
                    className={errors.relationType ? "border-red-500" : ""}
                  />
                  {errors.relationType && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.relationType}
                    </p>
                  )}
                </div>

                {/* Doğum Tarihi */}
                <div className="event-endsAt-fix">
                  <Label
                    htmlFor="birthDate"
                    className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Doğum Tarihi
                  </Label>
                  <ChakraDatePicker
                    value={formData.birthDate || ""}
                    onChange={(value) => handleInputChange("birthDate", value)}
                    placeholder="Doğum tarihi seçin"
                    hasError={!!errors.birthDate}
                    maxDate={new Date()}
                  />
                  {errors.birthDate && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.birthDate}
                    </p>
                  )}
                  <style>{`
                        .event-endsAt-fix .react-datepicker__close-icon {
                          right: 34px;
                          top: 50%;
                          transform: translateY(-50%);
                          height: 100%;
                          display: flex;
                          align-items: center;
                        }
                        .event-endsAt-fix .react-datepicker__close-icon::after {
                          position: static;
                          transform: none;
                          margin: 0;
                        }
                      `}</style>
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#9B51E0] hover:bg-[#8B4AD9] text-white"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Kaydediliyor...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Save className="w-4 h-4 mr-2" />
                      Kaydet
                    </div>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentEditForm;
